import { useState, useEffect } from 'react';
import { dailySpin, getSpinStatus, getSpinConfig } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useLang } from '../lib/lang';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { RotateCw, Gift } from 'lucide-react';

export default function SpinWheel() {
  const { user, refreshUser } = useAuth();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [canSpin, setCanSpin] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [reward, setReward] = useState(null);
  const [rewardCredit, setRewardCredit] = useState(null);
  const [coinName, setCoinName] = useState('Luxorcoin');
  const [segments, setSegments] = useState([]);

  useEffect(() => {
    getSpinConfig().then(r => {
      setSegments(r.data.segments || []);
      setEnabled(r.data.enabled);
      setCoinName(r.data.coin_name || 'Luxorcoin');
    }).catch(() => {});
    if (user) {
      getSpinStatus().then(r => setCanSpin(r.data.can_spin && r.data.enabled)).catch(() => {});
    }
  }, [user]);

  const handleSpin = async () => {
    if (!user || spinning || !canSpin || segments.length === 0) return;
    setSpinning(true);
    setReward(null);
    setRewardCredit(null);
    try {
      const res = await dailySpin();
      const won = res.data.reward;
      const wonCredit = res.data.reward_credit;
      const cName = res.data.coin_name || coinName;
      const winIdx = res.data.segment_index;
      const segAngle = 360 / segments.length;
      const targetAngle = 360 * 6 + (360 - (winIdx * segAngle + segAngle / 2));
      setRotation(prev => prev + targetAngle);
      setTimeout(() => {
        setReward(won);
        setRewardCredit(wonCredit);
        setSpinning(false);
        setCanSpin(false);
        refreshUser();
        toast.success(`+${won} ${cName}${won > 1 ? 's' : ''} = +£${wonCredit} de crédit !`);
      }, 4500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Spin failed');
      setSpinning(false);
    }
  };

  if (!user || !enabled) return null;

  const defaultColors = ['#D4AF37', '#F6EED7', '#B88A2A', '#EAD7A2'];

  return (
    <>
      {canSpin && (
        <button onClick={() => setOpen(true)} className="fixed bottom-6 left-6 z-40 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-110 transition-transform animate-bounce" data-testid="spin-wheel-trigger">
          <Gift className="w-6 h-6" />
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-border sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl text-center">{t('dailySpin')}</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center py-4">
            <div className="relative w-72 h-72 mb-6">
              {/* Arrow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-10 w-0 h-0 border-l-[12px] border-r-[12px] border-t-[24px] border-l-transparent border-r-transparent border-t-red-500" />
              <svg viewBox="0 0 200 200" className="w-full h-full" style={{ transform: `rotate(${rotation}deg)`, transition: spinning ? 'transform 4.5s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none' }}>
                {segments.map((seg, i) => {
                  const total = segments.length;
                  const angle = (360 / total) * i;
                  const nextAngle = (360 / total) * (i + 1);
                  const rad = (angle * Math.PI) / 180;
                  const nextRad = (nextAngle * Math.PI) / 180;
                  const x1 = 100 + 95 * Math.cos(rad);
                  const y1 = 100 + 95 * Math.sin(rad);
                  const x2 = 100 + 95 * Math.cos(nextRad);
                  const y2 = 100 + 95 * Math.sin(nextRad);
                  const midRad = ((angle + 360 / total / 2) * Math.PI) / 180;
                  const tx = 100 + 62 * Math.cos(midRad);
                  const ty = 100 + 62 * Math.sin(midRad);
                  const color = seg.color || defaultColors[i % defaultColors.length];
                  return (
                    <g key={i}>
                      <path d={`M100,100 L${x1},${y1} A95,95 0 0,1 ${x2},${y2} Z`} fill={color} stroke="#fff" strokeWidth="1.5" />
                      <text x={tx} y={ty} textAnchor="middle" dominantBaseline="middle" fill="#14161A" fontSize="13" fontWeight="800" transform={`rotate(${angle + 360 / total / 2}, ${tx}, ${ty})`}>
                        {seg.value}
                      </text>
                    </g>
                  );
                })}
                <circle cx="100" cy="100" r="20" fill="#14161A" />
                <text x="100" y="100" textAnchor="middle" dominantBaseline="middle" fill="#D4AF37" fontSize="8" fontWeight="700">SPIN</text>
              </svg>
            </div>
            {reward !== null ? (
              <div className="text-center">
                <p className="font-display text-4xl font-bold text-primary">+{reward} {coinName}{reward > 1 ? 's' : ''} !</p>
                {rewardCredit !== null && rewardCredit > 0 && (
                  <p className="font-mono text-2xl font-bold text-emerald-600 mt-2">= +£{rewardCredit} de crédit</p>
                )}
                <p className="text-sm text-muted-foreground mt-2">{t('spinRewardMsg')}</p>
                <Button className="mt-4 rounded-xl" onClick={() => setOpen(false)}>{t('close')}</Button>
              </div>
            ) : (
              <Button onClick={handleSpin} disabled={spinning || !canSpin} className="bg-primary text-primary-foreground hover:bg-primary/90 btn-press rounded-xl gap-2 text-base px-8 h-12">
                <RotateCw className={`w-5 h-5 ${spinning ? 'animate-spin' : ''}`} />
                {spinning ? t('spinning') : t('spinNow')}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
